// Libraries
import { useEffect } from 'react';

import playSound from '../../../utils/misc/playSound';
// Hooks
import { useAppSelector } from '../../../app/hooks';
import useSound from '../../../hooks/useSound';

// Features
import { selectUser } from '../../../features/user/userSlice';
import { selectSession } from '../../../features/session/sessionSlice';

// Styles
import styles from './CurrentBall.module.css';

// Constants
const balls = [
    {
        multiplier: 1,
        letter: 'B',
        color: 'blue',
    },
    {
        multiplier: 2,
        letter: 'I',
        color: 'red',
    },
    {
        multiplier: 3,
        letter: 'N',
        color: 'white',
    },
    {
        multiplier: 4,
        letter: 'G',
        color: 'green',
    },
    {
        multiplier: 5,
        letter: 'O',
        color: 'yellow',
    },
];

/**
 * @function CurrentBall
 * Describes the current number in the draw sequence
 *
 * @example
 * return (
 *   <CurrentBall number={0} animation={true} length='short' />
 * )
 */
function CurrentBall({
    number,
    animation,
    length,
}: CurrentBallProps): JSX.Element {
    const user = useAppSelector(selectUser);
    const session = useAppSelector(selectSession);
    const voice = user.voice !== '' ? user.voice : 'default';
    const ballSoundUrl = `https://${process.env.REACT_APP_CDN}/play-bingo/audio/${voice}/${number}.mp3`;
    const ballSound = useSound(ballSoundUrl);
    const quotient = Math.ceil(number / 15);
    const ball = balls.find(({ multiplier }) => multiplier === quotient);

    useEffect(() => {
        if (
            number !== 0 &&
            user.sound_on === true &&
            session?.session.is_live_stream !== true
        ) {
            ballSound.src = ballSoundUrl;
            playSound(ballSound);
        }
    }, [number]);

    if (number === 0 || ball === undefined)
        return <figure className={styles.figure}></figure>;

    return (
        <figure key={number} className={styles.figure}>
            <img
                src={`https://${process.env.REACT_APP_CDN}/play-bingo/images/graphics/graphic-ball-${ball.color}.svg`}
                alt={`Ball ${number}`}
                className={styles.img}
            />
            <figcaption
                className={`${styles.figcaption} ${styles[ball.color]}`}
            >
                <span className={styles.letter}>{ball.letter}</span>
                <span className={styles.number}>{number}</span>
            </figcaption>
            {animation && (
                <svg
                    viewBox="0 0 100 100"
                    className={`${styles.countdown} ${styles[length]}`}
                >
                    <path d="M 50,50 m 0,-47 a 47,47 0 1 1 0,94 a 47,47 0 1 1 0,-94"></path>
                </svg>
            )}
        </figure>
    );
}

/**
 * @interface CurrentBallProps
 * Describes the properties required for the CurrentBall component
 *
 * @property {number} number Current number in draw sequence
 * @property {boolean} animation Whether to show a countdown animation
 * @property {string} length Length of countdown animation
 */
export interface CurrentBallProps {
    number: number;
    animation: boolean;
    length: 'short' | 'long';
}

// Exports
export default CurrentBall;
